import { useEffect, useRef, useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import { PUBLIC_URL } from '../../../__common/env';
import ImageModel from './ImageModel';
import { FaExpandAlt } from 'react-icons/fa';
import { useMainContext } from '../../../hooks/useMainContext';
import { observer } from 'mobx-react-lite';
import { Checkbox, message, Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { Utils } from '../../../utils/Utils';
import { tempSvgUrl } from '../../../constant';
import { MdOutlineEdit } from 'react-icons/md';
import RoundedButton from '../../Globle/RoundedButton';
import Button from '../../Globle/Button';
import { STEP_TYPE } from '../../../__utils/UiConst';

pdfjsLib.GlobalWorkerOptions.workerSrc =
    'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.10.38/pdf.worker.min.mjs';

const SelectPage = observer(() => {
    const { viewManager, closetManager, uiManager, matrixManager } =
        useMainContext();
    const [loading] = useState(false);
    const [lastSelectedPage, setLastSelectedPage] = useState(null);
    const [focusedPageNum, setFocusedPageNum] = useState(null);
    const [selectAllChecked, setSelectAllChecked] = useState(false);
    const pdfFile = viewManager.pdfFile || closetManager.pdfBuffer;
    const inputRefs = useRef({});
    useEffect(() => {
        if (focusedPageNum !== null && inputRefs.current[focusedPageNum]) {
            inputRefs.current[focusedPageNum].focus();
        }
    }, [focusedPageNum]);

    useEffect(() => {
        if (pdfFile) {
            if (!viewManager.pdfDoc) {
                Utils.readPdf(pdfFile).then((pdfDoc) => {
                    const pagesDemoList = Array.from(
                        { length: pdfDoc.numPages },
                        (_, index) => ({
                            pageNum: index + 1,
                            name: `Page ${index + 1}`,
                            imageUrl: tempSvgUrl,
                            tempUrl: true,
                        }),
                    );

                    viewManager.setUiPages(pagesDemoList);
                    viewManager.setPdfDoc(pdfDoc);
                    viewManager.renderAllPages(viewManager.pdfDoc);
                });
                return;
            }
            viewManager.renderAllPages(viewManager.pdfDoc);
        }
    }, [pdfFile, viewManager, uiManager.isPdfUploadModel]);

    useEffect(() => {
        const getTotalPages = async () => {
            if (viewManager.pdfFile) {
                try {
                    const objectUrl = URL.createObjectURL(viewManager.pdfFile);
                    const pdfDoc =
                        await pdfjsLib.getDocument(objectUrl).promise;
                    const totalPages = pdfDoc.numPages;
                    const allSelected =
                        totalPages > 0 &&
                        viewManager.selectedPages.length === totalPages;
                    setSelectAllChecked(allSelected);
                    // Clean up the object URL after we're done
                    URL.revokeObjectURL(objectUrl);
                } catch (error) {
                    console.error('Error getting PDF pages:', error);
                    message.error('Error getting PDF pages', error);
                }
            }
        };
        getTotalPages();
    }, [viewManager.selectedPages, viewManager.pdfFile]);

    const toggleSelectPage = (pageNum, name, event) => {
        let updatedPages = [...viewManager.selectedPages];
        const isAlreadySelected = updatedPages.some(
            (p) => p.pageNum == pageNum,
        );

        // Get last selected page based on the most recent one in the list
        const selectedSorted = [...updatedPages].sort(
            (a, b) => b.pageNum - a.pageNum,
        );
        const lastCheckedPage =
            selectedSorted.length > 0 ? selectedSorted[0].pageNum : null;

        if (event.shiftKey && lastCheckedPage !== null) {
            const minPage = Math.min(lastCheckedPage, pageNum);
            const maxPage = Math.max(lastCheckedPage, pageNum);

            const pagesInRange = viewManager.uiPages
                .filter(
                    (page) =>
                        page.pageNum >= minPage && page.pageNum <= maxPage,
                )
                .map(({ pageNum, name }) => ({ pageNum, name }));

            // Ensure no duplicates and merge properly
            const pageNumsSet = new Set(updatedPages.map((p) => p.pageNum));
            pagesInRange.forEach((page) => pageNumsSet.add(page.pageNum));

            updatedPages = viewManager.uiPages
                .filter((p) => pageNumsSet.has(p.pageNum))
                .map(({ pageNum, name }) => ({ pageNum, name }));
        } else {
            // Regular toggle selection
            updatedPages = isAlreadySelected
                ? updatedPages.filter((p) => p.pageNum != pageNum)
                : [...updatedPages, { pageNum, name }];
        }

        updatedPages = updatedPages.reduce((unique, page) => {
            if (
                !unique.some(
                    (p) =>
                        String(p.pageNum) === String(page.pageNum) &&
                        p.name === page.name,
                )
            ) {
                unique.push(page);
            }
            return unique;
        }, []);

        viewManager.setSelectedPages(updatedPages);
    };

    const handleExpand = (imageUrl, name, pageNum) => {
        viewManager.setModelOpen(true);
        viewManager.setImageData({ imageUrl, name, pageNum });
        if (viewManager.pdfDoc) {
            viewManager.renderPage();
        }
    };

    const handleUploadAgain = () => {
        viewManager.setPdfFile(null);
        closetManager.setPdfBuffer(null);
        matrixManager.setPdfBuffer(null);
    };
    const handleFinalize = async () => {
        if (viewManager.selectedPages.length === 0) {
            message.error('No pages selected');
            return;
        }
        closetManager.excludeClosets();
        viewManager.setSelectedMenu(2);
        try {
            viewManager.setHasUserFinalized(true);
            if (!viewManager.projectId) {
                try {
                    await viewManager.uploadPdf(closetManager.pdfBuffer);
                    closetManager.setSelectedPageNo(
                        viewManager.selectedPages[0].pageNum,
                    );
                    uiManager.setIsPdfUploadModel(false);
                    uiManager.setSelectedStep(STEP_TYPE.MAPPING);
                    viewManager.createPageStatus();
                    try {
                        await viewManager.showPossibleClosets();
                    } catch (showClosetsError) {
                        console.error(
                            'Failed to show possible closets:',
                            showClosetsError.message,
                        );
                        throw showClosetsError;
                    }
                } catch (uploadError) {
                    console.error('Failed to upload PDF:', uploadError.message);
                    throw uploadError;
                }
            } else {
                await viewManager.updatePages();
                closetManager.setSelectedPageNo(
                    viewManager.selectedPages[0].pageNum,
                );
                uiManager.setIsPdfUploadModel(false);
                uiManager.setSelectedStep(STEP_TYPE.MAPPING);
                viewManager.createPageStatus();
                try {
                    await viewManager.showPossibleClosets();
                } catch (showClosetsError) {
                    console.error(
                        'Failed to show possible closets:',
                        showClosetsError.message,
                    );
                    throw showClosetsError;
                }
            }
        } catch (generalError) {
            console.error(
                'An unexpected error occurred:',
                generalError.message,
            );
            message.error('An unexpected error occurred. Please try again.');
        }
    };
    return (
        <>
            <div className="mt-[16px]">
                <div className="flex justify-between items-center">
                    <p className="font-favorit text-[16px] font-[400] leading-[24px] text-theme-black ">
                        {viewManager.uiPages.length} Pages
                    </p>
                    <div className="flex gap-[8px] items-baseline ">
                        <Checkbox
                            checked={selectAllChecked}
                            onChange={(e) => {
                                e.stopPropagation();
                                if (viewManager.isLoading) return;
                                setSelectAllChecked(e.target.checked);
                                viewManager.setSelectedPages(
                                    e.target.checked ? viewManager.uiPages : [],
                                );
                            }}
                            className="custom-checkbox"
                        />
                        <p className="font-favorit text-[16px] font-[400] leading-[24px] text-theme-black ">
                            Select All
                        </p>
                    </div>
                </div>

                {loading ? (
                    <div className="h-[calc(100vh-244px)] flex justify-center items-center">
                        <Spin
                            indicator={
                                <LoadingOutlined
                                    style={{ fontSize: 48, color: '#f46147' }}
                                    spin
                                />
                            }
                        />
                    </div>
                ) : (
                    <div
                        id="pdfContainer"
                        className="grid grid-cols-6 gap-4  pr-[16px] h-[calc(100vh-275px)] overflow-y-scroll pt-[24px]">
                        {viewManager.uiPages.map(
                            ({ pageNum, name, imageUrl }) => {
                                const isSelected =
                                    viewManager.selectedPages.some(
                                        (page) => page.pageNum == pageNum,
                                    );

                                return (
                                    <div
                                        key={pageNum}
                                        onMouseLeave={() => {
                                            setFocusedPageNum(null);
                                            inputRefs.current[pageNum]?.blur();
                                        }}
                                        className="flex flex-col items-center border-[1px] border-theme-border rounded-[16px] max-h-[256px] h-[256px] group  w-full overflow-hidden">
                                        <div
                                            onClick={(event) =>
                                                toggleSelectPage(
                                                    pageNum,
                                                    name,
                                                    event,
                                                )
                                            }
                                            className="relative cursor-pointer  w-full transition-all duration-300 ease-in-out">
                                            {!isSelected && (
                                                <div className="absolute top-0 left-0 w-full h-[200px] bg-[rgba(0,0,0,0.5)] opacity-0 group-hover:opacity-100 transition-opacity duration-300 ease-in-out z-0"></div>
                                            )}
                                            <div
                                                className={`absolute top-[8px] px-[8px] flex items-center justify-between w-full opacity-0 ${isSelected ? 'opacity-100' : 'group-hover:opacity-100'} group-hover:opacity-100 transition-opacity duration-300 ease-in-out`}>
                                                <Checkbox
                                                    checked={isSelected}
                                                    onChange={(e) => {
                                                        e.stopPropagation();
                                                        if (
                                                            viewManager.isLoading
                                                        )
                                                            return;
                                                        toggleSelectPage(
                                                            pageNum,
                                                            name,
                                                            e,
                                                        );
                                                    }}
                                                    className="custom-checkbox page-checkbox z-20"
                                                />
                                                <RoundedButton
                                                    className="z-20"
                                                    isActive={true}
                                                    onClick={(e) => {
                                                        e.stopPropagation();

                                                        handleExpand(
                                                            imageUrl,
                                                            name,
                                                            Number(pageNum),
                                                        );
                                                    }}
                                                    icon={
                                                        <FaExpandAlt className="text-theme-white text-[20px]" />
                                                    }
                                                />
                                            </div>
                                            {isSelected && (
                                                <div className="absolute top-0 left-0 w-full h-full flex justify-center items-center bg-[#FAC8C0] bg-opacity-15"></div>
                                            )}
                                            <img
                                                src={imageUrl}
                                                alt={`Page ${pageNum}`}
                                                className=" w-full h-[200px] object-contain"
                                            />
                                        </div>
                                        <div
                                            className={`flex items-center justify-between w-full p-[16px]  ${isSelected ? 'bg-[#FAC8C0]' : 'group-hover:bg-theme-header-bg'} `}>
                                            <input
                                                ref={(el) =>
                                                    (inputRefs.current[
                                                        pageNum
                                                    ] = el)
                                                }
                                                type="text"
                                                value={name}
                                                maxLength={24}
                                                onChange={(e) => {
                                                    const value =
                                                        e.target.value;
                                                    if (value.length <= 24) {
                                                        viewManager.handlePageNameChange(
                                                            pageNum,
                                                            value,
                                                        );
                                                    }
                                                }}
                                                onBlur={() =>
                                                    setFocusedPageNum(null)
                                                }
                                                className=" p-2 rounded w-[calc(100%-40px)] h-[24px] text-[16px] leading-[16px] bg-transparent text-theme-black font-favorit font-normal focus:outline-none focus:ring-0 focus:border-[1px] focus:border-theme-border"
                                                placeholder="Enter page name (max 24 chars)"
                                            />
                                            <MdOutlineEdit
                                                onClick={() =>
                                                    setFocusedPageNum(pageNum)
                                                }
                                                className="w-[24px] h-[24px] cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity duration-300 ease-in-out"
                                            />
                                        </div>
                                    </div>
                                );
                            },
                        )}
                    </div>
                )}

                <ImageModel />
            </div>
            <div className="flex justify-end items-center gap-[8px] mt-[12px]">
                {/* <Button
                            icon={GrPowerReset}
                            iconClass="!text-[20px] !ml-0 !mr-1 mb-[2px]"
                            onClick={() => {
                                handleUploadAgain();
                            }}
                            label={'Upload Again'}
                            className="!w-fit !h-[32px] !bg-transparent !rounded-[8px] text-theme-black hover:!bg-theme-orange hover:text-white !pl-[8px]"
                        /> */}
                <Button
                    type="button"
                    loading={viewManager.isLoading}
                    onClick={handleFinalize}
                    label={'Confirm'}
                    className={` !w-[140px] flex items-center justify-center ${viewManager.selectedPages.length === 0 ? 'pointer-events-none !bg-[#F5F5F5] !text-[#CCCCCC]' : 'bg-theme-orange text-theme-white hover:bg-theme-orange-hover'}`}
                />
            </div>
        </>
    );
});

export default SelectPage;
